import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as countActions from "@rentiohq/shared-frontend/dist/redux/count/count.actions";
import * as countSelectors from "@rentiohq/shared-frontend/dist/redux/count/count.selectors";
import * as countUtils from "@rentiohq/shared-frontend/dist/redux/count/count.utils";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import { EContractFetchType } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.types";
import * as contractUtils from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.utils";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import { EDocumentCustomFilter } from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.types";
import * as documentUtils from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.utils";
import * as paymentOrderHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.hooks";
import * as propertyActions from "@rentiohq/shared-frontend/dist/reduxV2/property/property.actions";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as propertySelectors from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import { EPropertyFetchType } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.types";
import * as propertyUtils from "@rentiohq/shared-frontend/dist/reduxV2/property/property.utils";
import * as propertyAmenityApi from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.api";
import * as propertyAmenityHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import {
  AMENITY_NAMES,
  createAmenityItem,
  getAmenityNumberValue,
  getAmenityStringValue,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.utils";
import * as tasksHooks from "@rentiohq/shared-frontend/dist/reduxV2/task/task.hooks";
import { ETaskCustomFilter } from "@rentiohq/shared-frontend/dist/reduxV2/task/task.utils";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EPropertyForRentStatus,
  EPropertyMemberTypes,
  EPropertyTypeId,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { canAddPaymentOrders } from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import { parseIncomeRatioRanges } from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import { getMembersWithRole } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  capitalizeString,
  join,
} from "@rentiohq/shared-frontend/dist/utils/string.utils";
import { appendQueryParams } from "@rentiohq/shared-frontend/dist/utils/url.utils";
import {
  Error,
  Loading,
  MultiStepForm,
  Page,
  RentioInternalRenderer,
  TextStyle,
  check,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import utils from "@rentiohq/web-shared/dist/utils";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Outlet,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { IRootStore } from "redux/reducers";
import createEditPropertyInfoForm from "../../../../forms/editPropertyInfo";
import * as t from "../../../../services/translationService";
import { PropertyArchivedCard } from "./components/PropertyArchivedCard";
import { PropertyMeta } from "./components/PropertyMeta";
import { PropertyPhotoGalleryModal } from "./components/PropertyPhotoGalleryModal";
import { PropertyPhotosField } from "./components/PropertyPhotosField";

const formId = generateFormId();

export const PropertyDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  // State
  const [showPropertyInfoForm, setShowPropertyInfoForm] = useState(false);
  // When true: the RentMatch settings form was opened via the "publish" toggle,
  // so the publication choice must default to "Publish" regardless of the
  // property's current stored value.
  const [forcePublishRentMatch, setForcePublishRentMatch] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  // When true: open gallery immediately after a successful form save
  // Ref to the MultiStepForm instance so we can call handleExternalSubmit() directly
  const [secondaryActionNode, setSecondaryActionNode] =
    useState<React.ReactNode>(undefined);

  // Redux
  const {
    detail: property,
    isFetching,
    fetchError,
    refetch,
  } = propertyHooks.useDetail({
    shouldRefetch: true,
    id: propertyId,
  });

  const contractsExtraFilterData = propertyId
    ? contractUtils.getFilterForProperty(propertyId, EContractFetchType.Active)
    : undefined;

  const { count: currentContractsCount } = contractHooks.useCount({
    shouldRefetch: true,
    // TODO: should we add refetch?
    //refetch: true,
    query: { filter: contractsExtraFilterData },
  });

  const { user, isBroker, broker } = authHooks.useSelf();
  // Extract broker URL fields for the RentMatch modal placeholder (must be
  // before early returns so TypeScript recognises them as read) // cspell:ignore recognises
  const brokerListingUrlTemplate = broker?.listingUrlTemplate;
  const brokerWebsiteUrl = broker?.websiteUrl;

  const { internalModeEnabled } = useInternalMode();

  const hasPaymentV2ForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.PaymentV2,
  );
  const hasRentioMoveForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.RentioMove,
  );
  const hasBookkeepingFeature = brokerHooks.useBrokerFeature(
    EBrokerFeature.Bookkeeping,
  );
  const hasRentMatchForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.RentMatch,
  );

  // const showPaymentV1Tab = internalModeEnabled;
  const showPaymentV2Tab = internalModeEnabled;

  const extraFilterData = { propertyId: { eq: propertyId } };

  const { count: documentsForProperty = 0 } = documentHooks.useCount(
    documentUtils.getDocumentPagedQuery({
      extraFilterData,
      customFilters: [
        EDocumentCustomFilter.Active,
        EDocumentCustomFilter.ExcludeInternalDocuments,
      ],
    }),
  );

  const countIdentifierTemplateDocumentsForProperty = propertyId
    ? countUtils.getCountIdentifierTemplateDocumentsForProperty(propertyId)
    : undefined;

  const { items: propertyAmenities, refetch: refetchPropertyAmenities } =
    propertyAmenityHooks.useGetByPropertyId({
      propertyId,
    });

  const templateDocumentsForPropertyCount = useSelector((state: IRootStore) =>
    countSelectors.getCount(state, countIdentifierTemplateDocumentsForProperty),
  );
  const isFetchingTemplateDocumentsForPropertyCount = useSelector(
    (state: IRootStore) =>
      countSelectors.getIsFetchingCount(
        state,
        countIdentifierTemplateDocumentsForProperty,
      ),
  );

  const { update } = propertyHooks.useUpdate({
    id: propertyId,
  });

  const { archive } = propertyHooks.useArchive({
    id: propertyId,
  });

  const { unarchive } = propertyHooks.useUnarchive({
    id: propertyId,
  });

  const isGroup = property?.typeId === EPropertyTypeId.Group;

  const propertiesInGroupCount = useSelector((state: IRootStore) => {
    if (!isGroup) {
      return;
    }

    return propertySelectors.getCount(
      state,
      propertyId
        ? propertyUtils.getFilter(EPropertyFetchType.InGroup, propertyId)
        : undefined,
    );
  });
  const isFetchingPropertiesInGroupCount = useSelector((state: IRootStore) => {
    if (!isGroup) {
      return false;
    }

    return countSelectors.getIsFetchingCount(
      state,
      propertyId
        ? countUtils.getCountIdentifierPropertiesInGroup(propertyId)
        : undefined,
    );
  });

  const { countSteps } = applicationHooks.useCountSteps({
    shouldRefetch: true,
    query: isGroup
      ? undefined
      : {
          filter: {
            archivedAt: { is: null },
            propertyId: { eq: propertyId },
          },
        },
  });
  let activeApplicationsCount: number | undefined;
  if (countSteps) {
    Object.keys(countSteps.steps).forEach(key => {
      if (+key !== EIntakeStep.notEligible && +key !== EIntakeStep.tenant) {
        activeApplicationsCount = activeApplicationsCount || 0;
        activeApplicationsCount += countSteps.steps[+key];
      }
    });
  }

  const { count: activePaymentOrdersCountV2 } = paymentOrderHooks.useCount({
    shouldRefetch: true,
    query: {
      filter: { propertyId: { eq: propertyId }, completedAt: { is: null } },
    },
  });

  const { count: allForThisProperty } = tasksHooks.useCount({
    shouldRefetch: true,
    query: {
      filter: {
        propertyId: { eq: propertyId },
        finishedAt: { is: null },
      },
      customFilters: [ETaskCustomFilter.NotArchived],
      // rolesFilter: [ETaskMemberType.Executor, ETaskMemberType.Follower],
    },
  });

  // Active tab
  const generalIsActive = !!matchPath(
    "/properties/:propertyId/",
    location.pathname,
  );
  const groupIsActive = !!matchPath(
    "/properties/:propertyId/group-info",
    location.pathname,
  );
  const applicationsIsActive = !!matchPath(
    "/properties/:propertyId/applications",
    location.pathname,
  );
  const contractsIsActive = !!matchPath(
    "/properties/:propertyId/contracts",
    location.pathname,
  );
  const paymentsIsActive = !!matchPath(
    "/properties/:propertyId/payments",
    location.pathname,
  );
  // const paymentsIsActiveV1 = !!matchPath(
  //   "/properties/:propertyId/payments-v1",
  //   location.pathname,
  // );
  const paymentsIsActiveV2 = !!matchPath(
    "/properties/:propertyId/payments-v2",
    location.pathname,
  );
  const documentsIsActive = !!matchPath(
    "/properties/:propertyId/documents",
    location.pathname,
  );
  const extraIsActive = !!matchPath(
    "/properties/:propertyId/extra",
    location.pathname,
  );
  const historyIsActive = !!matchPath(
    "/properties/:propertyId/history",
    location.pathname,
  );
  const tasksIsActive = !!matchPath(
    "/properties/:propertyId/tasks",
    location.pathname,
  );

  useEffect(() => {
    if (!isGroup) {
      return;
    }

    if (isFetchingPropertiesInGroupCount) {
      return;
    }

    if (!propertyId) {
      return;
    }

    dispatch(
      propertyActions.getCountStart.getAction({
        query: propertyUtils.getFilter(EPropertyFetchType.InGroup, propertyId),
      }),
    );
  }, [propertyId, isGroup]);

  useEffect(() => {
    if (isFetchingTemplateDocumentsForPropertyCount) {
      return;
    }

    if (!propertyId) {
      return;
    }

    dispatch(
      countActions.getCount.actions.start(
        countUtils.getActionPayloadTemplateDocumentsForProperty(propertyId),
      ),
    );
  }, [propertyId]);

  // Render
  if (!property && isFetching) {
    return <Loading />;
  }

  if (!property && fetchError) {
    return <Error errors={[fetchError]} backLink="/properties" />;
  }

  if (!property || !user) {
    return null;
  }

  const { archivedAt, roles } = property;

  let documentsCount = undefined;
  if (templateDocumentsForPropertyCount !== undefined) {
    documentsCount = documentsForProperty + templateDocumentsForPropertyCount;
  }

  let tabs = [
    {
      name: "dashboard",
      permission: "propertyDashboard:visit",
      content: t.propertyDetailTabsInformation(),
      url: `/properties/${propertyId}/`,
      isActive: generalIsActive,
    },
    {
      name: "applications",
      permission: "propertyDashboard:visit",
      content: t.propertyDetailTabsApplications(),
      url: `/properties/${propertyId}/applications`,
      isActive: applicationsIsActive,
      count: activeApplicationsCount,
      showTab: !isGroup,
    },
    {
      name: "groupInfo",
      permission: "propertyDashboard:visit",
      content: getLocalizedText("property.detail.tabs.group_info"),
      url: `/properties/${propertyId}/group-info`,
      isActive: groupIsActive,
      count: propertiesInGroupCount,
      showTab: isGroup,
    },

    {
      name: "contracts",
      permission: "propertyContracts:visit",
      content: t.propertyDetailTabsContracts(),
      url: `/properties/${propertyId}/contracts`,
      isActive: contractsIsActive,
      count: currentContractsCount,
      showTab: !isGroup,
    },
    {
      name: "payments",
      permission: "propertyPayments:visit",
      content: t.propertyDetailTabsPayments(),
      url: `/properties/${propertyId}/payments`,
      isActive: paymentsIsActive,
      count: activePaymentOrdersCountV2,
      showTab: !isGroup,
    },
    // {
    //   name: "payments [V1]",
    //   permission: "propertyPayments:visit",
    //   content: `${t.propertyDetailTabsPayments()} [V1]`,
    //   url: `/properties/${propertyId}/payments-v1`,
    //   isActive: paymentsIsActiveV1,
    //   count: activePaymentOrdersCountV2,
    //   showTab: !isGroup && showPaymentV1Tab,
    // },
    {
      name: "payments [V2]",
      permission: "propertyPayments:visit",
      content: `${t.propertyDetailTabsPayments()} [V2]`,
      url: `/properties/${propertyId}/payments-v2`,
      isActive: paymentsIsActiveV2,
      count: activePaymentOrdersCountV2,
      showTab: !isGroup && showPaymentV2Tab,
    },
    {
      name: "tasks",
      permission: "propertyDashboard:visit",
      content: t.propertyDetailTabsTasks(),
      url: `/properties/${propertyId}/tasks`,
      isActive: tasksIsActive,
      count: allForThisProperty,
    },
    {
      name: "documents",
      permission: "propertyDocuments:visit",
      content: t.propertyDetailTabsDocuments(),
      url: `/properties/${propertyId}/documents`,
      isActive: documentsIsActive,
      count: documentsCount,
    },
    {
      name: "extra",
      permission: "propertyDashboard:visit",
      content: getLocalizedText("property.detail.tabs.extra"),
      url: `/properties/${propertyId}/extra`,
      isActive: extraIsActive,
      showTab: !isGroup || hasBookkeepingFeature,
    },
    {
      name: "history",
      permission: "propertyHistory:visit",
      content: t.propertyDetailTabsHistory(),
      url: `/properties/${propertyId}/history`,
      isActive: historyIsActive,
    },
  ].filter(({ showTab = true }) => showTab);

  tabs = tabs.filter((tab: any) => check(roles || [], tab.permission));

  const subtitle = property?.reference
    ? `${property.reference} | ${property.name}`
    : property.name;
  const handleForRentSelected = () => {
    if (hasRentMatchForBroker && !property.isRentMatchPublished) {
      setShowPropertyInfoForm(true);
    }
  };

  // Opens the RentMatch settings form from the pill (respects the stored
  // publication value - no forced default).
  const handleOpenRentMatchSettings = () => {
    setForcePublishRentMatch(false);
    setShowPropertyInfoForm(true);
  };

  // Driven by the publication toggle on the candidates card.
  // Turning it on opens the settings form pre-set to "Publish"; turning it off
  // asks for confirmation and then unpublishes directly.
  const handleRentMatchPublishToggle = (nextPublished: boolean) => {
    if (nextPublished) {
      setForcePublishRentMatch(true);
      setShowPropertyInfoForm(true);
      return;
    }

    confirm({
      title: getLocalizedText("rentmatch.dashboard.unpublish_confirm.title"),
      info: getLocalizedText("rentmatch.dashboard.unpublish_confirm.info"),
      primaryActions: [
        {
          title: getLocalizedText(
            "rentmatch.dashboard.unpublish_confirm.action",
          ),
          onPress: () => {
            update({ data: { isRentMatchPublished: false } });
          },
        },
      ],
      secondaryAction: {
        title: getLocalizedText("system.cancel"),
        onPress: () => {},
      },
    });
  };

  const roomCountBedroom =
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountBedroom) ??
    property.roomCountBedroom;

  // The RentMatch settings pill only opens the settings form for admins
  // (matches the previous kebab-menu gating); for others it is status-only.
  const canEditRentMatch =
    hasRentMatchForBroker &&
    !archivedAt &&
    property.roles.includes(EPropertyMemberTypes.Admin);

  const metadata = (
    <PropertyMeta
      property={property}
      onForRentSelected={handleForRentSelected}
      roomCountBedroom={roomCountBedroom}
      hasRentMatch={hasRentMatchForBroker}
      onRentMatchSettingsClick={
        canEditRentMatch ? handleOpenRentMatchSettings : undefined
      }
    />
  );

  const editProperty = () => {
    navigate(`/properties/${property.id}/edit`);
  };

  const handleClickArchiveProperty = () => {
    if (!propertyId) {
      return;
    }

    if (archivedAt) {
      unarchive({});
    } else {
      archive({});
    }
  };

  const handleCreateDocumentPackageClick = () => {
    navigate(
      appendQueryParams({
        path: "/forms/document-package",
        queryParams: {
          propertyId,
        },
      }),
    );
  };

  const handleAddTaskClick = () => {
    navigate(
      appendQueryParams({
        path: "/tasks/add",
        queryParams: {
          propertyId,
        },
      }),
    );
  };

  const handlePressRequestMoveContractForEmptyProperty = () => {
    if (!hasRentioMoveForBroker) {
      return;
    }

    const tenantNames = join(
      getMembersWithRole(property.members, EPropertyMemberTypes.Tenant).map(x =>
        getName(x.account),
      ),
    );
    const ownerNames = join(
      getMembersWithRole(property.members, EPropertyMemberTypes.Owner).map(x =>
        getName(x.account),
      ),
    );

    confirm({
      title: capitalizeString(
        getLocalizedText(
          "contract.move.request_move_contract_for_empty_property.confirm.title",
        ),
      ),
      info: capitalizeString(
        getLocalizedText(
          "contract.move.request_move_contract_for_empty_property.confirm.info",
          {
            email: user?.email || "?",
          },
        ),
      ),
      inputValueProps: {
        type: "string",
        asMultiline: true,
        label: getLocalizedText(
          "contract.move.request_move_contract_for_empty_property.remarks.label",
        ),
        initialValue: getLocalizedText(
          "contract.move.request_move_contract_for_empty_property.remarks.initial_value",
          {
            tenantName: tenantNames,
            tenantAddress: formatAddress(property),
            tenantVat: "",
            tenantDateOfBirth: "",

            ownerName: ownerNames,
          },
        ),
        optional: true,
      },
      primaryActions: [
        {
          title: getLocalizedText("system.request"),
          onPress: async ({ inputValue } = {}) => {
            try {
              await api.post(
                `/properties/${propertyId}/request-move-request-contract`,
                {
                  data: {
                    extraRemarks: inputValue,
                  },
                  isV2: true,
                },
              );

              showAlert({
                type: "success",
                message: getLocalizedText(
                  "contract.move.request_move_contract_for_empty_property.alert.success",
                ),
              });
            } catch (unknownError) {
              const error = unknownError as any;
              showAlert({
                type: "error",
                error,
                message: getLocalizedText(
                  "contract.move.request_move_contract_for_empty_property.alert.failure",
                ),
              });
            }
          },
        },
      ],
    });
  };

  const pageVariation = archivedAt ? "error" : undefined;
  const dropdownLabel = archivedAt ? t.unarchive() : t.archive();
  const dropdownLabelVariation = archivedAt ? undefined : "negative";
  const dropdown = [];
  if (!archivedAt) {
    dropdown.push({
      content: <TextStyle>{t.propertyEditDropdownLabel()}</TextStyle>,
      onClick: () => editProperty(),
    });

    // For RentMatch brokers the property-info form holds the RentMatch
    // settings; it is surfaced as a prominent header button (see `actions`)
    // instead of being hidden in this kebab menu. Non-RentMatch brokers keep
    // the generic property-info edit entry here.
    if (
      property?.roles.includes(EPropertyMemberTypes.Admin) &&
      !hasRentMatchForBroker
    ) {
      dropdown.push({
        content: <TextStyle>{t.propertyInfoEditDropdownLabel()}</TextStyle>,
        onClick: () => {
          setShowPropertyInfoForm(true);
        },
      });
    }

    dropdown.push({
      content: (
        <TextStyle variation={dropdownLabelVariation}>
          {dropdownLabel}
        </TextStyle>
      ),
      onClick: handleClickArchiveProperty,
    });

    dropdown.push({
      content: getLocalizedText("properties.duplicate"),
      onClick: () => {
        navigate(`/properties/${property.id}/duplicate`);
      },
    });

    if (hasRentioMoveForBroker) {
      dropdown.push({
        content: getLocalizedText(
          "contract.move.request_move_contract_for_empty_property.cta.title",
        ),
        onClick: handlePressRequestMoveContractForEmptyProperty,
      });
    }
  }

  const hasOwners =
    getMembersWithRole(property.members, EPropertyMemberTypes.Owner).length > 0;
  const secondaryActions = archivedAt
    ? undefined
    : [
        ...(hasOwners
          ? [
              {
                content: (
                  <TextStyle>
                    {t.propertyDetailAddBasicContractInfoAction()}
                  </TextStyle>
                ),
                url: `/properties/${propertyId}/contracts/add`,
              },
            ]
          : []),
        ...(canAddPaymentOrders({
          property,
          isBroker,
        })
          ? [
              {
                content: (
                  <TextStyle>
                    {t.propertyDetailAddPaymentOrderAction()}
                  </TextStyle>
                ),
                url: hasPaymentV2ForBroker
                  ? `/properties/${propertyId}/payments-v2/add`
                  : `/properties/${propertyId}/payments/add`,
              },
            ]
          : []),
        {
          content: <TextStyle>{t.propertyDetailAddTaskAction()}</TextStyle>,
          onClick: handleAddTaskClick,
        },
        {
          content: <TextStyle>{t.documentPackageCreateAction()}</TextStyle>,
          onClick: handleCreateDocumentPackageClick,
        },
      ];

  // Opens the multi-photo gallery modal when the cover photo is clicked.
  const handleImageClick = () => {
    if (propertyId) {
      setShowPhotoGallery(true);
    }
  };

  const handleEditPropertyInfoSuccess = async (values: any) => {
    // IMPORTANT: sanitizeValue() in form.utils.ts converts null/"" → undefined,
    // and undefined keys disappear from partialData during Redux spread-merge.
    // That means cleared fields are simply absent from `values`.
    // We must explicitly fall back to null (not undefined) for every nullable
    // field so the API receives null and clears the value.
    const nullablePropertyFields = [
      "price",
      "costs",
      "availableAtDate",
      "externalListingUrl",
      "epcLabel",
      "buildingTerraceSurfaceArea",
      "buildingGardenSurfaceArea",
    ];

    // Amenity-backed fields — read with null fallback
    const roomCountBedroom = values.roomCountBedroom ?? null;
    const roomCountBathroom = values.roomCountBathroom ?? null;
    const roomCountGarage = values.roomCountGarage ?? null;
    const livingSurfaceArea = values.livingSurfaceArea ?? null;
    const descriptionLong = values.descriptionLong ?? null;
    const virtualTourUrl = values.virtualTourUrl ?? null;
    // One field on screen, two amenity rows underneath, so it is split apart
    // again here. An emptied half arrives as null and is stored as null, which
    // is what puts the property back on the default it was scored on before.
    const rentmatchScore = values.rentmatchScore || {};
    const rentmatchPerfectIncome = rentmatchScore.perfectIncome ?? null;
    const rentmatchMaxHousehold = rentmatchScore.maxHousehold ?? null;

    // Build restValues: everything except the amenity-backed fields, then
    // explicitly patch in null for any nullable property field that was cleared.
    const {
      roomCountBedroom: _rcb,
      roomCountBathroom: _rcba, // cspell:ignore rcba
      roomCountGarage: _rcg,
      livingSurfaceArea: _lsa,
      descriptionLong: _description,
      virtualTourUrl: _virtualTour,
      rentmatchScore: _rentmatchScore,
      ...restValues
    } = values;

    for (const field of nullablePropertyFields) {
      if (!(field in restValues) || restValues[field] === undefined) {
        restValues[field] = null;
      }
    }

    // Use createAmenityItem (same as /extra page) so existing amenity IDs are
    // included in the upsert, enabling correct update vs. insert behaviour. // cspell:ignore behaviour
    const amenitiesToUpsert = [
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.roomCountBedroom,
        roomCountBedroom,
        "number",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.roomCountBathroom,
        roomCountBathroom,
        "number",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.roomCountGarage,
        roomCountGarage,
        "number",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.livingSurfaceArea,
        livingSurfaceArea,
        "number",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.descriptionLong,
        descriptionLong,
        "string",
      ),
      // The same amenity the property's Extra tab writes, so a link entered
      // while publishing and one entered on that tab are one value.
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.virtualTourUrl,
        virtualTourUrl,
        "string",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.minimumIncome,
        rentmatchPerfectIncome,
        "number",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.maxOccupants,
        rentmatchMaxHousehold,
        "number",
      ),
    ].filter((item): item is NonNullable<typeof item> => item !== null);

    if (amenitiesToUpsert.length > 0 && propertyId) {
      await propertyAmenityApi.upsertForProperty(propertyId, {
        amenities: amenitiesToUpsert,
      });
      refetchPropertyAmenities();
    }

    // When the broker enables RentMatch publication, automatically set
    // the property status to "For Rent" so both fields stay in sync.
    if (restValues.isRentMatchPublished === true) {
      restValues.forRentStatus = EPropertyForRentStatus.ForRent;
    }

    update({
      data: restValues,
    });

    setShowPropertyInfoForm(false);
    setForcePublishRentMatch(false);
  };

  const showAsArchived = property.archivedAt && !historyIsActive;

  return (
    <Page
      title={formatAddress(property, true, true, false)}
      subtitle={subtitle}
      image={utils.properties.getImageForProperty(property)}
      onImageClick={handleImageClick}
      loading={false}
      error={undefined}
      breadcrumbs={{
        to: "/properties",
        content: t.propertyDetailBreadcrumb(),
      }}
      metadata={metadata}
      dropdown={dropdown}
      tabs={tabs}
      secondaryActions={secondaryActions}
      secondaryActionNode={secondaryActionNode}
      variation={pageVariation}
    >
      {showAsArchived && <PropertyArchivedCard property={property} />}

      {showAsArchived && internalModeEnabled && (
        <>
          <RentioInternalRenderer>
            Showing archived property
          </RentioInternalRenderer>
          <Outlet />
        </>
      )}

      {!showAsArchived && (
        <Outlet
          context={{
            setSecondaryActionNode,
            rentMatch: hasRentMatchForBroker
              ? {
                  canEdit: canEditRentMatch,
                  onTogglePublish: handleRentMatchPublishToggle,
                }
              : undefined,
          }}
        />
      )}

      {showPropertyInfoForm && property ? (
        <MultiStepForm
          formId={`property-info-${formId}`}
          schemas={createEditPropertyInfoForm({
            property: {
              ...property,
              roomCountBedroom:
                getAmenityNumberValue(
                  propertyAmenities,
                  AMENITY_NAMES.roomCountBedroom,
                ) ?? undefined,
              // Opened via the "publish" toggle: default the choice to Publish.
              ...(forcePublishRentMatch ? { isRentMatchPublished: true } : {}),
            },
            hasRentMatchForBroker,
            listingUrlTemplate: brokerListingUrlTemplate,
            websiteUrl: brokerWebsiteUrl,
            // Amenity-sourced values for extra property details (step 2)
            roomCountBathroom:
              getAmenityNumberValue(
                propertyAmenities,
                AMENITY_NAMES.roomCountBathroom,
              ) ?? null,
            roomCountGarage:
              getAmenityNumberValue(
                propertyAmenities,
                AMENITY_NAMES.roomCountGarage,
              ) ?? null,
            livingSurfaceArea:
              getAmenityNumberValue(
                propertyAmenities,
                AMENITY_NAMES.livingSurfaceArea,
              ) ?? null,
            descriptionLong: getAmenityStringValue(
              propertyAmenities,
              AMENITY_NAMES.descriptionLong,
            ),
            virtualTourUrl: getAmenityStringValue(
              propertyAmenities,
              AMENITY_NAMES.virtualTourUrl,
            ),
            rentmatchPerfectIncome: getAmenityNumberValue(
              propertyAmenities,
              AMENITY_NAMES.minimumIncome,
            ),
            rentmatchMaxHousehold: getAmenityNumberValue(
              propertyAmenities,
              AMENITY_NAMES.maxOccupants,
            ),
            // Parsed here rather than in the form, so the field can show what
            // this property asks today with nothing set.
            rentmatchIncomeRanges: parseIncomeRatioRanges(
              broker?.rentmatchIncomeRanges,
            ),
            propertyId,
            onPhotosSaved: () => refetch(),
          })}
          // The gallery is drawn by a field of this form, so it can stand in
          // the media section instead of after every other question. It was a
          // button here that saved, closed and reopened as a second modal, then
          // the modal's footer (DEV-3180).
          extraFields={{
            [ERentioFormField.PropertyPhotos]: PropertyPhotosField,
          }}
          withAside={false}
          asModal={true}
          onSuccess={handleEditPropertyInfoSuccess}
          modalProps={{
            shouldCloseOnOverlayClick: true,
            // The form is four cards, and cards on white are not cards.
            hasGreyBody: true,
            onClose: () => {
              setShowPropertyInfoForm(false);
              setForcePublishRentMatch(false);
            },
          }}
          submitLabel={t.system("save")}
          isLoading={false}
        />
      ) : null}

      {showPhotoGallery && propertyId ? (
        <PropertyPhotoGalleryModal
          propertyId={propertyId}
          onClose={() => setShowPhotoGallery(false)}
          onSaved={() => refetch()}
        />
      ) : null}
    </Page>
  );
};
